/* eslint-disable react/jsx-handler-names */
import React, { useState, useEffect, useContext, useMemo } from 'react';

import * as yup from 'yup';
import { useFormik } from 'formik';
import { useTranslation } from 'react-i18next';

import AdminService from '../../../../../services/AdminService';
import FormDropdown from '../../../../Form/FormDropdown';
import TextInput from '../../../../Form/TextInput';
import SidePopup from '../../SidePopup';
import PhoneInput from '../../../../Form/PhoneInput';

import yupSchemaToFields from '../../../../../helpers/yupSchemaToFields';
import { UiContext } from '../../../../../context/UiContext';
import useSwitchableRowData from '../../../../../hooks/useSwitchableRowData';
import classes from './styles.module.scss';

export default function EditUser({
  isVisible,
  handleClose,
  users,
  selectedRows,
  refetch,
  noAnimation,
}) {
  const [isTriedToSubmit, setIsTriedToSubmit] = useState(false);

  const { t } = useTranslation();

  const {
    showModal,
    setIsFetching,
    isCreatingOrUpdating,
    setIsCreatingOrUpdating,
  } = useContext(UiContext);

  const { currentDataIndex, switchToPrevDataElement, switchToNextDataElement } =
    useSwitchableRowData(users, selectedRows);

  const validationSchema = useMemo(
    () =>
      yup.object({
        firstName: yup
          .string()
          .trim()
          .required(t('common.requiredField'))
          .matches(/^[\p{L}\p{M}'-]+$/u, t('common.onlyOneWordAllowed')),
        lastName: yup
          .string()
          .trim()
          .required(t('common.requiredField'))
          .matches(/^[\p{L}\p{M}'-]+$/u, t('common.onlyOneWordAllowed')),
        middleName: yup
          .string()
          .trim()
          .nullable()
          .matches(/^[\p{L}\p{M}'-]*$/u, {
            message: t('common.onlyOneWordAllowed'),
            excludeEmptyString: true,
          }),
        email: yup
          .string()
          .trim()
          .email(
            t(
              'dashboardComponents.Console.UserManagement.EditUser.errorMessages.validEmail'
            )
          )
          .required(t('common.requiredField')),
        designation: yup
          .string()
          .trim()
          .nullable()
          .optional()
          .max(
            100,
            t(
              'dashboardComponents.Console.UserManagement.NewUserForm.errorMessages.designationMaxLength'
            )
          ),
        phone: yup
          .string()
          .trim()
          .required(t('common.cannotBeEmpty'))
          .test(
            'is-valid-phone',
            t(
              'dashboardComponents.Console.CompanyManagement.addEdit.errorMessages.phoneInvalid'
            ),
            async (value) => {
              if (!value) {
                return true; // Skip validation if the value is empty
              }

              const { isValidPhoneNumber, AsYouType } = await import(
                'libphonenumber-js'
              );

              const formattedNumber = new AsYouType().input(value);

              const formattedNumberWithPlus = formattedNumber.startsWith('+')
                ? formattedNumber
                : `+${formattedNumber}`;

              return isValidPhoneNumber(
                new AsYouType().input(formattedNumberWithPlus)
              );
            }
          ),
        role: yup
          .object()
          .required(
            t(
              'dashboardComponents.Console.UserManagement.EditUser.errorMessages.roleRequired'
            )
          ),
      }),
    [t]
  );

  const formik = useFormik({
    initialValues: {
      email: users?.[currentDataIndex]?.userName,
      firstName: users?.[currentDataIndex]?.userProfile.firstName,
      lastName: users?.[currentDataIndex]?.userProfile.lastName,
      phone: users?.[currentDataIndex]?.userProfile.phone,
      middleName: users?.[currentDataIndex]?.userProfile.middleName,
      role: users?.[currentDataIndex]?.roles.length
        ? {
            label: users?.[currentDataIndex]?.roles[0].name,
            value: users?.[currentDataIndex]?.roles[0].id,
          }
        : null,
      designation: users?.[currentDataIndex]?.userProfile.designation,
    },
    validationSchema,
    onSubmit: async (values) => {
      try {
        setIsFetching(true);
        setIsCreatingOrUpdating(true);

        const requestData = {
          firstName: values.firstName,
          lastName: values.lastName,
          email: values.email,
          phone: values.phone,
          middleName: values.middleName,
          roleIds: [values.role.value],
          id: users[currentDataIndex].id,
          designation: values.designation,
        };

        requestData.cityIds = [];
        requestData.locationIds = [];

        await AdminService.updateUser(requestData);

        await refetch();
        showModal({
          title: t(
            'dashboardComponents.Console.UserManagement.EditUser.successModal.title'
          ),
          text: t(
            'dashboardComponents.Console.UserManagement.EditUser.successModal.text'
          ),
          onCancel: handleClose,
        });
      } catch (error) {
        console.log(error);
        showModal({
          icon: 'error',
          title: t(
            'dashboardComponents.Console.UserManagement.EditUser.errorModal.title'
          ),
          text: t(
            'dashboardComponents.Console.UserManagement.EditUser.errorModal.text'
          ),
        });
      } finally {
        setIsFetching(false);
        setIsCreatingOrUpdating(false);
      }
    },
    enableReinitialize: true,
  });

  useEffect(() => {
    if (!isVisible) {
      formik.resetForm();
      setIsTriedToSubmit(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isVisible]);

  useEffect(() => {
    setIsTriedToSubmit(false);
  }, [currentDataIndex]);

  return (
    <SidePopup
      formik={formik}
      fields={yupSchemaToFields(validationSchema)}
      mode="edit"
      isVisible={isVisible}
      handleClose={handleClose}
      title={t('dashboardComponents.Console.UserManagement.EditUser.title')}
      hasNoAnimation={noAnimation}
      onLeftButtonClick={formik.resetForm}
      onRightButtonClick={() => {
        setIsTriedToSubmit(true);
        formik.handleSubmit();
      }}
      isRightButtonDisabled={
        !formik.values.firstName?.trim() ||
        !formik.values.lastName?.trim() ||
        !formik.values.email?.trim() ||
        !formik.values.phone?.trim() ||
        !formik.values.role ||
        !formik.values.designation?.trim() ||
        isCreatingOrUpdating
      }
      switchNext={selectedRows.length > 1 && switchToNextDataElement}
      switchPrev={selectedRows.length > 1 && switchToPrevDataElement}
    >
      <div className={classes.EditUser}>
        <form onSubmit={formik.handleSubmit}>
          <div className={classes.col}>
            <TextInput
              error={formik.errors.firstName}
              touched={isTriedToSubmit}
              value={formik.values.firstName}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              label={t(
                'dashboardComponents.Console.UserManagement.EditUser.labels.firstName'
              )}
              name="firstName"
              placeholder={t(
                'dashboardComponents.Console.UserManagement.EditUser.placeholders.firstName'
              )}
              height={50}
            />
            <TextInput
              error={formik.errors.lastName}
              touched={isTriedToSubmit}
              value={formik.values.lastName}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              label={t(
                'dashboardComponents.Console.UserManagement.EditUser.labels.lastName'
              )}
              name="lastName"
              placeholder={t(
                'dashboardComponents.Console.UserManagement.EditUser.placeholders.lastName'
              )}
              height={50}
            />

            <FormDropdown
              error={formik.errors.role}
              touched={isTriedToSubmit}
              value={formik.values.role}
              setFieldValue={formik.setFieldValue}
              onBlur={formik.handleBlur}
              label={t(
                'dashboardComponents.Console.UserManagement.EditUser.labels.role'
              )}
              name="role"
              placeholder={t(
                'dashboardComponents.Console.UserManagement.EditUser.placeholders.role'
              )}
              height={50}
              width="100%"
              fetchOptions={{
                dataName: 'rolesOptionsUser',
                getDataHandler: AdminService.getRolesUser,
              }}
              searchInfo={t(
                'dashboardComponents.Console.UserManagement.EditUser.searchInfo.role'
              )}
              searchInfoWidth={173}
              setFieldTouched={formik.setFieldTouched}
            />
            <PhoneInput
              name="phone"
              label={t('intelCandidate.form.personal.phone')}
              placeholder="+1 555 555 5555"
              setFieldValue={formik.setFieldValue}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              touched={isTriedToSubmit}
              error={formik.errors.phone}
              value={formik.values.phone}
              height={45}
            />
          </div>
          <div className={classes.col}>
            <TextInput
              error={formik.errors.middleName}
              touched={isTriedToSubmit}
              value={formik.values.middleName}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              label={t(
                'dashboardComponents.Console.UserManagement.EditUser.labels.middleName'
              )}
              name="middleName"
              placeholder={t(
                'dashboardComponents.Console.UserManagement.EditUser.placeholders.middleName'
              )}
              height={50}
            />
            <TextInput
              error={formik.errors.designation}
              touched={isTriedToSubmit}
              value={formik.values.designation}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              label={t(
                'dashboardComponents.Console.UserManagement.EditUser.labels.designation'
              )}
              name="designation"
              placeholder={t(
                'dashboardComponents.Console.UserManagement.EditUser.placeholders.designation'
              )}
              height={50}
              note={t('common.optional')}
            />
            <TextInput
              error={formik.errors.email}
              touched={isTriedToSubmit}
              value={formik.values.email}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              label={t(
                'dashboardComponents.Console.UserManagement.EditUser.labels.email'
              )}
              name="email"
              placeholder={t(
                'dashboardComponents.Console.UserManagement.EditUser.placeholders.email'
              )}
              height={50}
            />
          </div>
        </form>
      </div>
    </SidePopup>
  );
}
